import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import ProductsApi from "api/products";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import StockApi from "api/stock";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import SupplierApi from "api/supplier";
import Icon from "@mui/material/Icon";

function NewStock() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State to manage stock information and form validation
  const [stockItems, setStockItems] = useState([
    { id: Date.now(), product_id: null, quantity: "", supplier: null },
  ]);
  const [loading, setLoading] = useState(false); // Loading state for product fetching
  const [loadingSave, setLoadingSave] = useState(false); // Loading state for saving stock
  const [products, setProducts] = useState([]); // List of products for selection
  const [errors, setErrors] = useState({}); // Validation errors
  const [suppliers, setSuppliers] = useState([]); // Suppliers list

  // Fetch suppliers from the API
  useEffect(() => {
    const fetchSuppliers = async () => {
      const token = localStorage.getItem("token");
      try {
        const supplierData = await SupplierApi.GetSuppliersList(token);
        setSuppliers(supplierData.data.results);
      } catch (error) {
        console.error("Failed to fetch suppliers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  // Fetch products when the component mounts
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found in localStorage.");
      return;
    }

    setLoading(true);
    ProductsApi.GetAllProducts(token, true)
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Validate form inputs
  const validate = () => {
    let valid = true;
    const newErrors = {};

    stockItems.forEach((item, index) => {
      if (!item.product_id) {
        newErrors[`product_id_${index}`] = `${t("Le produit est requis !")}`;
        valid = false;
      }
      if (!item.quantity) {
        newErrors[`quantity_${index}`] = `${t("La quantité est requise !")}`;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleAddItem = () => {
    setStockItems([...stockItems, { id: Date.now(), product_id: null, quantity: "", supplier: null }]);
  };

  const handleRemoveItem = (id) => {
    if (stockItems.length > 1) {
      setStockItems(stockItems.filter((item) => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setErrors({});
    setStockItems(
      stockItems.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  // Save stock information
  const handleSave = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found in localStorage.");
      return;
    }

    if (!validate()) {
      return;
    }

    setLoadingSave(true);

    const payload = {
      initial_stock_items: stockItems.map((item) => ({
        product_id: item.product_id.value,
        quantity: Number(item.quantity),
        ...(item.supplier && {
          stock_supplier_name: item.supplier.label,
        }),
      })),
    };

    StockApi.SetInitialStock(payload, token)
      .then((response) => {
        navigate("/gestion-de-stock/situation-de-stock");
      })
      .catch((error) => {
        console.error(error.response?.data?.msg || error.message);
      })
      .finally(() => {
        setLoadingSave(false);
      });
  };

  const handleCreateSupplier = () => {
    navigate("/gestion-de-stock/nouveau-fournisseur");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        minHeight="85vh"
      >
        <SoftBox py={3} mt={5}>
          <Grid container justifyContent="center" sx={{ height: "100%" }}>
            <Grid item xs={12}>
              <Card elevation={3} sx={{ borderRadius: "16px" }}>
                <CardContent sx={{ p: 0 }}>
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={3}
                  >
                    <SoftTypography variant="h6" fontWeight="medium">
                      {t("Nouveau stock")}
                    </SoftTypography>
                    <SoftButton
                      variant="gradient"
                      color="info"
                      onClick={handleAddItem}
                    >
                      <Icon sx={{ fontWeight: "bold", mr: 1 }}>add</Icon>
                      {t("Ajouter un produit")}
                    </SoftButton>
                  </SoftBox>

                  <SoftBox mt={3}>
                    {stockItems.map((item, index) => (
                      <Grid container spacing={2} key={item.id} alignItems="flex-start" sx={{
                        mb: 3,
                        pb: 3,
                        borderBottom: index !== stockItems.length - 1 ? "1px solid #f0f2f5" : "none"
                      }}>
                        <Grid item xs={12} sm={5}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Produit")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftSelect
                              label={t("Produit")}
                              placeholder={t("Sélectionner un produit")}
                              value={item.product_id}
                              onChange={(newValue) =>
                                handleItemChange(item.id, "product_id", newValue)
                              }
                              options={
                                loading
                                  ? [
                                    {
                                      value: "",
                                      label: (
                                        <SoftBox
                                          display="flex"
                                          justifyContent="center"
                                          alignItems="center"
                                          minWidth="100%"
                                          minHeight="48px"
                                        >
                                          <CircularProgress
                                            size={20}
                                            sx={{
                                              color: "#1F51FF",
                                              marginLeft: 7,
                                            }}
                                          />
                                        </SoftBox>
                                      ),
                                    },
                                  ]
                                  : (products || []).length > 0
                                    ? products.map((product) => ({
                                      value: product.id,
                                      label: product.product_name,
                                    }))
                                    : [
                                      {
                                        value: "",
                                        label: `${t("Sélectionner un produit")}`,
                                      },
                                    ]
                              }
                            />
                            {errors[`product_id_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`product_id_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Valeur de stock")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftInput
                              type="number"
                              placeholder={t("Valeur de stock")}
                              value={item.quantity}
                              onChange={(e) => {
                                const value = Math.max(1, e.target.value);
                                handleItemChange(item.id, "quantity", value);
                              }}
                              error={!!errors[`quantity_${index}`]}
                            />
                            {errors[`quantity_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`quantity_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Fournisseur")}
                              </SoftTypography>
                              <SoftTypography
                                onClick={handleCreateSupplier}
                                variant="button"
                                color="info"
                                fontWeight="medium"
                                sx={{ cursor: "pointer", fontSize: "0.65rem" }}
                              >
                                + {t("Nouveau")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftSelect
                              label={t("Fournisseur")}
                              placeholder={t("Sélectionner un fournisseur")}
                              value={item.supplier}
                              onChange={(newValue) => handleItemChange(item.id, "supplier", newValue)}
                              options={(suppliers || []).map((supplier) => ({
                                value: supplier.id,
                                label: supplier.name,
                              }))}
                            />
                          </SoftBox>
                        </Grid>
                        <Grid item xs={12} sm={1}>
                          <SoftBox
                            mt={3.5}
                            display="flex"
                            justifyContent="center"
                          >
                            <SoftTypography
                              variant="body1"
                              color="error"
                              sx={{
                                cursor: "pointer",
                                lineHeight: 0,
                                p: 1,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#fff5f5" }
                              }}
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <Icon>delete</Icon>
                            </SoftTypography>
                          </SoftBox>
                        </Grid>
                      </Grid>
                    ))}
                  </SoftBox>

                  <SoftBox mt={5} display="flex" justifyContent="flex-end">
                    <SoftButton
                      disabled={loadingSave}
                      type="submit"
                      variant="gradient"
                      color="dark"
                      onClick={handleSave}
                      sx={{ px: 5, borderRadius: "8px" }}
                    >
                      {loadingSave ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        `${t("Enregistrer")}`
                      )}
                    </SoftButton>
                  </SoftBox>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
        <Footer />
      </SoftBox>
    </DashboardLayout>
  );
}

export default NewStock;
